import React, { useEffect, useState } from 'react';
import { useQuery } from "react-query";
import { useLocation, useNavigate } from 'react-router-dom';
import OutlineButton from "../../components/customButtons/OutlineButton";
import Loader from "../../components/loader";
import ROUTES from "../../constants/routes";
import { getPaymentStatus } from "../../apis/paymobPayment";

// Paymob's webhook can reach the backend a few seconds after it redirects the
// customer here, so keep asking while the payment is still pending
const POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 30000;

const SuccessIcon = () => (
  <svg className="h-64 w-64 text-green-600 mx-auto my-4" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">  <path stroke="none" d="M0 0h24v24H0z"/>  <circle cx="12" cy="12" r="9" />  <path d="M9 12l2 2l4 -4" /></svg>
);

const FailedIcon = () => (
  <svg className="h-64 w-64 text-red-600 mx-auto my-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">  <circle cx="12" cy="12" r="10" />  <line x1="15" y1="9" x2="9" y2="15" />  <line x1="9" y1="9" x2="15" y2="15" /></svg>
);

const PaymentStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Paymob adds its order id to the redirect; the result itself comes from the API
  const orderId = new URLSearchParams(location.search).get("order");
  const [timedOut, setTimedOut] = useState(false);

  const { data: status, isLoading, isError } = useQuery(
    ["payment-status", orderId],
    () => getPaymentStatus(orderId),
    {
      enabled: Boolean(orderId),
      retry: false,
      refetchInterval: (data) =>
        data === "pending" && !timedOut ? POLL_INTERVAL_MS : false,
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), MAX_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  if (orderId && (isLoading || (status === "pending" && !timedOut))) {
    return (
      <div className="bg-gray-100 h-screen">
        <div className="bg-white p-6 md:mx-auto text-center">
          <Loader className="my-10" />
          <p className="text-gray-600">Confirming your payment…</p>
        </div>
      </div>
    );
  }

  const success = status === "success";
  const stillPending = status === "pending";

  let title = "Payment Failed!";
  let message = "Please try again later.";
  if (success) {
    title = "Payment Done!";
    message = "Thank you for completing your secure online payment.";
  } else if (stillPending) {
    title = "Payment Being Confirmed";
    message = "We haven't received the confirmation yet. Check your profile in a few minutes; you'll also get an email.";
  } else if (!orderId || isError) {
    title = "Payment Not Found";
    message = "We couldn't find this payment. Check your profile or contact us if you were charged.";
  }

  return (
    <div className="bg-gray-100 h-screen">
      <div className="bg-white p-6  md:mx-auto">
        {success ? <SuccessIcon /> : <FailedIcon />}
        <div className="text-center">
          <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">{title}</h3>
          <p className="text-gray-600 my-2">{message}</p>
          <p> {success ? "Have a great day!" : "Thank you!"}  </p>
          <OutlineButton
            className="px-12 py-3 my-10"
            onClick={() => navigate(success || stillPending ? ROUTES.PROFILE.path : ROUTES.CART.path)}
          >
            {success || stillPending ? "GO TO PROFILE" : "GO TO CART"}
          </OutlineButton>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
